"use client";

import { useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Typewriter — time-based character reveal for streaming text.
//
// Uses rAF with time-based delays to reveal characters one at a time.
// When the buffer of unrevealed characters grows (stream faster than reveal),
// the delay smoothly decreases toward `minDelay` to catch up.
//
// Multiple characters can be processed per rAF frame via a while-loop
// that drains all characters whose delay has elapsed, making the speed
// truly frame-rate independent.
// ---------------------------------------------------------------------------

interface TypewriterProps {
  /** Text to reveal. Can grow over time (streaming). */
  content: string;
  /** Base delay between characters in ms. Default: 20. */
  charDelay?: number;
  /** Minimum delay during catch-up in ms. Default: 2. */
  minDelay?: number;
  /** Rate at which delay decreases per buffered character. Default: 0.3. */
  catchUpRate?: number;
  /** Show blinking cursor. Default: true. */
  cursor?: boolean;
  className?: string;
}

export const Typewriter = ({
  content,
  charDelay = 20,
  minDelay = 2,
  catchUpRate = 0.3,
  cursor = true,
  className,
}: TypewriterProps) => {
  const [displayed, setDisplayed] = useState("");
  const indexRef = useRef(0);
  const lastCharTimeRef = useRef(0);
  const rafRef = useRef(0);
  const contentRef = useRef(content);

  useEffect(() => {
    // Sync ref inside effect so rAF loop always reads the latest content
    contentRef.current = content;
  });

  useEffect(() => {
    const step = (now: number) => {
      const target = contentRef.current;
      let index = indexRef.current;

      if (index >= target.length) {
        // Caught up — stop the loop, will restart when content grows
        rafRef.current = 0;
        return;
      }

      // Cap drift so a long background tab doesn't dump everything at once
      if (now - lastCharTimeRef.current > 200) {
        lastCharTimeRef.current = now - 200;
      }

      // Drain all characters whose time has come (multiple per frame if needed)
      while (index < target.length) {
        const remaining = target.length - index;
        const delay = Math.max(minDelay, charDelay - remaining * catchUpRate);

        if (now - lastCharTimeRef.current < delay) {
          break;
        }

        // Advance clock by the delay (not `now`) for precise accumulation
        lastCharTimeRef.current += delay;
        index += 1;
      }

      if (index !== indexRef.current) {
        indexRef.current = index;
        setDisplayed(target.slice(0, index));
      }

      rafRef.current = requestAnimationFrame(step);
    };

    // Start the loop if there are unrevealed characters
    if (indexRef.current < contentRef.current.length && !rafRef.current) {
      lastCharTimeRef.current = performance.now();
      rafRef.current = requestAnimationFrame(step);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [content, charDelay, minDelay, catchUpRate]);

  return (
    <span className={className}>
      <span className="inline whitespace-pre-wrap">
        {displayed}
        {cursor && (
          <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-current align-middle" />
        )}
      </span>
    </span>
  );
};
